<script setup lang="ts">
import type { EventVault } from "@/models"
import { computed } from "vue"
import { DrawerContextCard, EventExtendCard } from "@/components"
import { Drawer, NoData } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { EventVaultStatus } from "@/models"
import { EventExtendDrawerKey } from "@/symbols"
import { convertDateTimezone } from "@/utils"

interface EventExtendDrawerProps {
  eventVault?: EventVault
  isLoading?: boolean
}

const props = withDefaults(defineProps<EventExtendDrawerProps>(), { isLoading: false })

const { d, t } = useTranslation()

const { toggle } = useModal(EventExtendDrawerKey)

const isStatusExpired = computed(() => props.eventVault?.status === EventVaultStatus.Expired)
const isStatusExpiring = computed(() => props.eventVault?.status === EventVaultStatus.Expiring)

const title = computed(() => {
  if (isStatusExpired.value) {
    return t("event_extend_drawer.title_expired")
  }
  if (isStatusExpiring.value) {
    return t("event_extend_drawer.title_expired_soon")
  }
  return "Extend Event"
})

const formattedExpiryDate = computed(() => {
  if (!props.eventVault?.expiryDate)
    return null

  return props.eventVault.timezone
    ? d(convertDateTimezone(props.eventVault.expiryDate, props.eventVault.timezone), "short")
    : d(props.eventVault.expiryDate, "short")
})
</script>

<template>
  <Drawer :name="EventExtendDrawerKey" :title="title">
    <div v-if="isLoading" class="flex flex-col gap-5">
      <div class="gpx-skeleton w-full h-24" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-full h-12" />
      <div class="gpx-skeleton w-1/3 h-12" />
    </div>
    <div v-else-if="eventVault">
      <DrawerContextCard :event-vault="eventVault" />
      <div v-if="!isStatusExpired && !isStatusExpiring">
        <NoData
          :headline="t('event_extend_drawer.no_data_headline')"
          :content="t('event_extend_drawer.no_data_content')" :button-label="t('close')" @click="toggle"
        />
      </div>
      <div v-else class="flex flex-col gap-5 font-montserrat">
        <i18n-t
          v-if="formattedExpiryDate"
          keypath="event_extend_drawer.expiry_date"
          scope="global"
          tag="p"
        >
          <template #date>
            <span class="font-semibold">
              {{ formattedExpiryDate }}
            </span>
          </template>
        </i18n-t>

        <div class="flex flex-col gap-2">
          <h4 class="font-semibold">
            {{ t("event_extend_drawer.keep_content_question") }}
          </h4>

          <EventExtendCard :event-vault="eventVault" />
        </div>
      </div>
    </div>
    <NoData v-else :button-label="t('close')" @click="toggle" />
  </Drawer>
</template>
